import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { Loader2, RotateCcw, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useRestoreVersion, useVersions } from '../hooks/useVersions';
import { ConfirmDialog } from './ConfirmDialog';
/**
 * Version history side-panel with accessible restore confirmation.
 * Uses ConfirmDialog instead of window.confirm, and asks the parent to
 * re-mount the editor (via onRestored) rather than reloading the page.
 */ export function VersionHistoryPanel({ documentId, onClose, onRestored }) {
    const { data, isLoading } = useVersions(documentId);
    const versions = data?.versions;
    const currentVersionId = data?.currentVersionId;
    const restore = useRestoreVersion();
    const [target, setTarget] = useState(null);
    async function doRestore() {
        if (!target) return;
        try {
            await restore.mutateAsync({
                documentId,
                versionId: target.id,
                baseVersionId: currentVersionId
            });
            toast.success(`Restored v${target.versionNumber}`);
            onRestored();
        } catch (err) {
            if (err instanceof ConnectError && err.code === Code.Aborted) {
                toast.error('Someone saved a newer version. Reload and try again.');
            } else {
                toast.error(`Restore failed: ${err.message}`);
            }
        }
    }
    const restoredFrom = (v)=>v.restoredFromVersionId ? versions?.find((o)=>o.id === v.restoredFromVersionId)?.versionNumber : undefined;
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col h-full",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between px-4 py-2 border-b",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-sm font-semibold",
                        children: "Version history"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: onClose,
                        "aria-label": "Close version history",
                        className: "text-muted-foreground hover:text-foreground",
                        children: /*#__PURE__*/ _jsx(X, {
                            className: "w-4 h-4"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex-1 overflow-y-auto",
                children: isLoading ? /*#__PURE__*/ _jsx("div", {
                    className: "flex items-center justify-center py-8",
                    children: /*#__PURE__*/ _jsx(Loader2, {
                        className: "w-5 h-5 animate-spin text-muted-foreground"
                    })
                }) : !versions || versions.length === 0 ? /*#__PURE__*/ _jsx("p", {
                    className: "p-4 text-sm text-muted-foreground",
                    children: "No versions yet."
                }) : /*#__PURE__*/ _jsx("ul", {
                    className: "divide-y",
                    children: versions.map((v)=>/*#__PURE__*/ _jsx("li", {
                            className: "px-4 py-3",
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-0",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-2 text-sm font-medium",
                                                children: [
                                                    "v",
                                                    v.versionNumber,
                                                    v.id === currentVersionId && /*#__PURE__*/ _jsx("span", {
                                                        className: "rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
                                                        children: "Current"
                                                    })
                                                ]
                                            }),
                                            restoredFrom(v) && /*#__PURE__*/ _jsxs("div", {
                                                className: "text-xs text-muted-foreground",
                                                children: [
                                                    "restored from v",
                                                    restoredFrom(v)
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "text-xs text-muted-foreground truncate",
                                                children: [
                                                    new Date(v.createdAt).toLocaleString(),
                                                    " · ",
                                                    v.createdByUserId
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("button", {
                                        type: "button",
                                        onClick: ()=>setTarget(v),
                                        disabled: restore.isPending,
                                        className: "inline-flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50",
                                        children: [
                                            /*#__PURE__*/ _jsx(RotateCcw, {
                                                className: "w-3 h-3"
                                            }),
                                            "Restore"
                                        ]
                                    })
                                ]
                            })
                        }, v.id))
                })
            }),
            /*#__PURE__*/ _jsx(ConfirmDialog, {
                open: !!target,
                onOpenChange: (open)=>!open && setTarget(null),
                title: `Restore v${target?.versionNumber ?? ''}?`,
                description: "A new version will be created containing the bytes of the selected one. The current editor session will reload so collaborators see the restored content.",
                confirmLabel: "Restore",
                onConfirm: doRestore
            })
        ]
    });
}
